import React from 'react';
import { getCurrentSeason, getSeasonTitle } from '../../utils/currentSeason';

const MediaTags = ({ media, mediaType, showAchievements = false }) => {
  const achievementPercentage =
    media.type === 'SteamGame' && media.total_achievements
      ? (media.achievements_unlocked / media.total_achievements * 100).toFixed(2)
      : 0;

  const renderTags = () => {
    const tags = [];

    if (media.type === 'SteamGame') {
      if (media.status === 'Completed') {
        tags.push(
          <span className="tag" key="completed">
            <img src="/static/icons/trophy-solid.svg" className="icon" alt="Completed" />
            Completed
          </span>
        );
      }
      tags.push(
        <span className="tag" key="playtime">
          {media.playtime ? (
            <>
              <img src="/static/icons/clock-solid.svg" className="icon" alt="Playtime" />
              Playtime: {(media.playtime / 60).toFixed(1)}h
            </>
          ) : (
            'No recorded playtime'
          )}
        </span>
      );
      if (media.playtime_windows && media.playtime_windows > 0) {
        tags.push(
          <span className="tag" key="playtime_windows">
            <img src="/static/icons/windows.svg" className="icon" alt="Windows Playtime" />
            {(media.playtime_windows / 60).toFixed(1)}h
          </span>
        );
      }
      if (media.playtime_deck && media.playtime_deck > 0) {
        tags.push(
          <span className="tag" key="playtime_deck">
            <img src="/static/icons/steamdeck.svg" className="icon" alt="Steam Deck Playtime" />
            {(media.playtime_deck / 60).toFixed(1)}h
          </span>
        );
      }
      if (showAchievements && media.total_achievements > 0) {
        tags.push(
          <div className="achievement-container" key="achievements">
            <div className="progress-bar">
              <div className="progress" style={{ width: `${achievementPercentage}%` }}></div>
            </div>
            <span className="achievement-text">
              {media.achievements_unlocked} / {media.total_achievements}
            </span>
          </div>
        );
      }
      if (media.total_achievements > 0 && media.achievements_unlocked >= media.total_achievements) {
        tags.push(
          <span className="tag" key="perfect_game">
            <img src="/static/icons/medal-solid.svg" className="icon" alt="Perfect Game" />
            Perfect Game
          </span>
        );
      }
    } else if (media.type === 'Anime' || media.type === 'AnimeMovie' || media.type === 'AnimeSerie') {
      const current = getCurrentSeason(media);
      const status = (current.status || 'Unknown').toLowerCase();
      if (status === 'plan to watch') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/list-solid.svg" className="icon" alt="Plan to Watch" />
            Plan to Watch
            {media.seasons?.length > 1 ? `: ${getSeasonTitle(current)}` : ''}
          </span>
        );
      } else if (status === 'watching') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/eye-solid.svg" className="icon" alt="Watching" />
            Watching: {media.seasons?.length > 1 ? `${getSeasonTitle(current)} – ` : ''}
            Ep. {current.current_episode || 0}
            {current.episode_count ? ` / ${current.episode_count}` : ''}
          </span>
        );
      } else if (status === 'on hold') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/eye-slash-solid.svg" className="icon" alt="On Hold" />
            On Hold: {media.seasons?.length > 1 ? `${getSeasonTitle(current)} – ` : ''}
            Ep. {current.current_episode || 0}
            {current.episode_count ? ` / ${current.episode_count}` : ''}
          </span>
        );
      } else if (status === 'forgotten') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/ghost-solid.svg" className="icon" alt="Forgotten" />
            Forgotten: {media.seasons?.length > 1 ? `${getSeasonTitle(current)} – ` : ''}
            Ep. {current.current_episode || 0}
            {current.episode_count ? ` / ${current.episode_count}` : ''}
          </span>
        );
      } else if (status === 'dropped') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/circle-xmark-solid.svg" className="icon" alt="Dropped" />
            Dropped: {media.seasons?.length > 1 ? `${getSeasonTitle(current)} – ` : ''}
            Ep. {current.current_episode || 0}
            {current.episode_count ? ` / ${current.episode_count}` : ''}
          </span>
        );
      } else if (status === 'finished') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/trophy-solid.svg" className="icon" alt="Finished" />
            Finished
          </span>
        );
      } else {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/clipboard-question-solid.svg" className="icon" alt="Unknown" />
            Unknown status
          </span>
        );
      }
      if (media.seasons?.length > 1) {
        tags.push(
          <span className="tag" key="seasons">
            <img src="/static/icons/clapperboard-solid.svg" className="icon" alt="Seasons" />
            {media.seasons.length} Seasons
          </span>
        );
      }
      if (media.mal_score) {
        tags.push(
          <span className="tag" key="mal_score">
            <img src="/static/icons/myanimelist.svg" className="icon" alt="MAL Score" />
            Score: {media.mal_score}
          </span>
        );
      }
    } else if (media.type === 'ScreenMedia') {
      const isTV = media.media_format?.toLowerCase() === 'tv';
      const hasMultipleSeasons = media.seasons?.length > 1;
      const current = getCurrentSeason(media);
      const status = (current.status || media.status || 'Unknown').toLowerCase();
      if (status === 'plan to watch') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/list-solid.svg" className="icon" alt="Plan to Watch" />
            Plan to Watch{hasMultipleSeasons ? `: ${getSeasonTitle(current)}` : ''}
          </span>
        );
      } else if (status === 'watching') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/eye-solid.svg" className="icon" alt="Watching" />
            Watching{isTV ? `: ${hasMultipleSeasons ? `${getSeasonTitle(current)} – ` : ''}Ep. ${current.current_episode ?? 0}${current.episode_count ? ` / ${current.episode_count}` : ''}` : ''}
          </span>
        );
      } else if (status === 'finished') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/trophy-solid.svg" className="icon" alt="Finished" />
            Finished
          </span>
        );
      } else if (status === 'on hold') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/eye-slash-solid.svg" className="icon" alt="On Hold" />
            On Hold{isTV && hasMultipleSeasons ? `: ${getSeasonTitle(current)}` : ''}
          </span>
        );
      } else if (status === 'dropped') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/circle-xmark-solid.svg" className="icon" alt="Dropped" />
            Dropped{isTV && hasMultipleSeasons ? `: ${getSeasonTitle(current)}` : ''}
          </span>
        );
      } else {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/clipboard-question-solid.svg" className="icon" alt="Unknown" />
            Unknown status
          </span>
        );
      }
      if (hasMultipleSeasons) {
        tags.push(
          <span className="tag" key="seasons">
            <img src="/static/icons/clapperboard-solid.svg" className="icon" alt="Seasons" />
            {media.seasons.length} Seasons
          </span>
        );
      }
      if (media.tmdb_score) {
        tags.push(
          <span className="tag" key="tmdb_score">
            <img src="/static/icons/star-half-stroke-solid.svg" className="icon" alt="TMDB Score" />
            TMDB: {media.tmdb_score}
          </span>
        );
      }
    } else if (media.type === 'Manga') {
      if (media.ownership_status === 'Wishlisted') {
        tags.push(
          <span className="tag" key="ownership">
            <img src="/static/icons/star-solid.svg" className="icon" alt="Wishlisted" />
            Wishlisted
          </span>
        );
      } else if (media.ownership_status === 'Collecting') {
        tags.push(
          <span className="tag" key="ownership">
            <img src="/static/icons/book-solid.svg" className="icon" alt="Collecting" />
            Collecting {media.owned_volume_count ? `${media.owned_volume_count}` : ''}
            {media.volume_count ? ` / ${media.volume_count}` : ''}
          </span>
        );
      } else if (media.ownership_status === 'Owned') {
        tags.push(
          <span className="tag" key="ownership">
            <img src="/static/icons/check-double-solid.svg" className="icon" alt="Owned" />
            Full Series Owned
          </span>
        );
      }
      if (media.status === 'Finished') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/trophy-solid.svg" className="icon" alt="Finished" />
            Finished
          </span>
        );
      } else if (media.status === 'Reading') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/book-open-solid.svg" className="icon" alt="Reading" />
            Reading: {media.current_volume !== null ? `vol. ${media.current_volume}, ` : ''}Ch. {media.current_chapter || 0}
          </span>
        );
      } else if (media.status === 'Dropped') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/circle-xmark-solid.svg" className="icon" alt="Dropped" />
            Dropped: {media.current_volume !== null ? `vol. ${media.current_volume}, ` : ''}Ch. {media.current_chapter || 0}
          </span>
        );
      } else if (media.status === 'On Hold') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/book-solid.svg" className="icon" alt="On Hold" />
            On Hold: {media.current_volume !== null ? `vol. ${media.current_volume}, ` : ''}Ch. {media.current_chapter || 0}
          </span>
        );
      } else if (media.status === 'Plan to Read') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/list-solid.svg" className="icon" alt="Plan to Read" />
            Plan to Read
          </span>
        );
      }
      if (media.mal_score) {
        tags.push(
          <span className="tag" key="mal_score">
            <img src="/static/icons/myanimelist.svg" className="icon" alt="MAL Score" />
            Score: {media.mal_score}
          </span>
        );
      }
      if (media.condition) {
        const conditionMap = {
          Excellent: { icon: '/static/icons/circle-check-solid.svg', text: 'Excellent Condition' },
          Good: { icon: '/static/icons/check-solid.svg', text: 'Good Condition' },
          Okay: { icon: '/static/icons/circle-solid.svg', text: 'Okay Condition' },
          Bad: { icon: '/static/icons/circle-xmark-solid.svg', text: 'Bad Condition' },
          Poor: { icon: '/static/icons/xmark-solid.svg', text: 'Poor Condition' },
        };
        const { icon, text } = conditionMap[media.condition] || {
          icon: '/static/icons/circle-solid.svg',
          text: media.condition,
        };
        tags.push(
          <span className="tag" key="condition">
            <img src={icon} className="icon" alt={text} />
            {text}
          </span>
        );
      }
    } else if (media.type === 'Book') {
      if (media.ownership_status === 'Wishlisted') {
        tags.push(
          <span className="tag" key="ownership">
            <img src="/static/icons/star-solid.svg" className="icon" alt="Wishlisted" />
            Wishlisted
          </span>
        );
      } else if (media.ownership_status === 'Collecting') {
        tags.push(
          <span className="tag" key="ownership">
            <img src="/static/icons/book-solid.svg" className="icon" alt="Collecting" />
            Collecting
          </span>
        );
      } else if (media.ownership_status === 'Owned') {
        tags.push(
          <span className="tag" key="ownership">
            <img src="/static/icons/check-double-solid.svg" className="icon" alt="Owned" />
            Owned
          </span>
        );
      }
      if (media.status === 'Finished') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/trophy-solid.svg" className="icon" alt="Finished" />
            Finished
          </span>
        );
      } else if (media.status === 'Reading') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/book-open-solid.svg" className="icon" alt="Reading" />
            Reading: p. {media.current_page || 0}{media.page_count ? ` / ${media.page_count}` : ''}
          </span>
        );
      } else if (media.status === 'Dropped') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/circle-xmark-solid.svg" className="icon" alt="Dropped" />
            Dropped: p. {media.current_page || 0}{media.page_count ? ` / ${media.page_count}` : ''}
          </span>
        );
      } else if (media.status === 'On Hold') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/book-solid.svg" className="icon" alt="On Hold" />
            On Hold: p. {media.current_page || 0}{media.page_count ? ` / ${media.page_count}` : ''}
          </span>
        );
      } else if (media.status === 'Plan to Read') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/list-solid.svg" className="icon" alt="Plan to Read" />
            Plan to Read
          </span>
        );
      }
      if (media.ol_score) {
        tags.push(
          <span className="tag" key="ol_score">
            <img src="/static/icons/star-half-stroke-solid.svg" className="icon" alt="Open Library Score" />
            Score: {media.ol_score.toFixed ? media.ol_score.toFixed(1) : media.ol_score}
          </span>
        );
      }
      if (media.condition) {
        const conditionMap = {
          Excellent: { icon: '/static/icons/circle-check-solid.svg', text: 'Excellent Condition' },
          Good: { icon: '/static/icons/check-solid.svg', text: 'Good Condition' },
          Okay: { icon: '/static/icons/circle-solid.svg', text: 'Okay Condition' },
          Bad: { icon: '/static/icons/circle-xmark-solid.svg', text: 'Bad Condition' },
          Poor: { icon: '/static/icons/xmark-solid.svg', text: 'Poor Condition' },
        };
        const { icon, text } = conditionMap[media.condition] || {
          icon: '/static/icons/circle-solid.svg',
          text: media.condition,
        };
        tags.push(
          <span className="tag" key="condition">
            <img src={icon} className="icon" alt={text} />
            {text}
          </span>
        );
      }
    } else if (
      ['PhysicalGame', 'PSPGame', 'NDSGame', 'GameboyGame', 'SwitchGame', 'PS3Game', 'VitaGame'].includes(media.type)
    ) {
      if (media.platform && media.type === 'PhysicalGame' && mediaType === 'PhysicalGame') {
        if (media.platform && media.type === 'PhysicalGame' && mediaType === 'PhysicalGame') {
          if (media.platform === 'psp') {
            tags.push(
              <span className="tag" key="platform">
                <img src="/static/icons/playstation.svg" className="icon" alt="Platform" />
                PSP
              </span>
            );
          } else if (media.platform === 'vita') {
            tags.push(
              <span className="tag" key="platform">
                <img src="/static/icons/playstation.svg" className="icon" alt="Platform" />
                PSVita
              </span>
            );
          } else if (media.platform === 'nds') {
            tags.push(
              <span className="tag" key="platform">
                <img src="/static/icons/nintendo-ds.svg" className="icon" alt="Platform" />
                NDS
              </span>
            );
          } else{
            tags.push(
              <span className="tag" key="platform">
                <img src="/static/icons/gamepad-solid.svg" className="icon" alt="Platform" />
                {media.platform.charAt(0).toUpperCase() + media.platform.slice(1)}
              </span>
            );
          }
        }
      }
      if (media.status === 'Completed') {
        tags.push(
          <span className="tag" key="completed">
            <img src="/static/icons/trophy-solid.svg" className="icon" alt="Completed" />
            Completed
          </span>
        );
      }
      if (media.ownership_status === 'Owned') {
        tags.push(
          <span className="tag" key="ownership">
            <img src="/static/icons/check-double-solid.svg" className="icon" alt="Owned" />
            Owned
          </span>
        );
      } else if (media.ownership_status === 'Wishlisted') {
        tags.push(
          <span className="tag" key="ownership">
            <img src="/static/icons/star-solid.svg" className="icon" alt="Wishlisted" />
            Wishlisted
          </span>
        );
      }
      if (media.condition) {
        const conditionMap = {
          Excellent: { icon: '/static/icons/circle-check-solid.svg', text: 'Excellent Condition' },
          Good: { icon: '/static/icons/check-solid.svg', text: 'Good Condition' },
          Okay: { icon: '/static/icons/circle-solid.svg', text: 'Okay Condition' },
          Bad: { icon: '/static/icons/circle-xmark-solid.svg', text: 'Bad Condition' },
          Poor: { icon: '/static/icons/xmark-solid.svg', text: 'Poor Condition' },
        };
        const { icon, text } = conditionMap[media.condition] || {
          icon: '/static/icons/circle-solid.svg',
          text: media.condition,
        };
        tags.push(
          <span className="tag" key="condition">
            <img src={icon} className="icon" alt={text} />
            {text}
          </span>
        );
      }
    } else if (['Music', 'Vinyl', 'CD'].includes(media.type)) {
      if (media.type === 'Music' && mediaType === 'Music' && media.format) {
        if (media.format === 'vinyl') {
          tags.push(
            <span className="tag" key="format">
              <img src="/static/icons/record-vinyl-solid.svg" className="icon" alt="Vinyl" />
              Vinyl
            </span>
          );
        } else if (media.format === 'cd') {
          tags.push(
            <span className="tag" key="format">
              <img src="/static/icons/compact-disc-solid.svg" className="icon" alt="CD" />
              CD
            </span>
          );
        }
      }
      if (media.ownership_status === 'Owned') {
        tags.push(
          <span className="tag" key="ownership">
            <img src="/static/icons/check-double-solid.svg" className="icon" alt="Owned" />
            Owned
          </span>
        );
      } else if (media.ownership_status === 'Wishlisted') {
        tags.push(
          <span className="tag" key="ownership">
            <img src="/static/icons/star-solid.svg" className="icon" alt="Wishlisted" />
            Wishlisted
          </span>
        );
      }
      if (media.condition) {
        const conditionMap = {
          Excellent: { icon: '/static/icons/circle-check-solid.svg', text: 'Excellent Condition' },
          Good: { icon: '/static/icons/check-solid.svg', text: 'Good Condition' },
          Okay: { icon: '/static/icons/circle-solid.svg', text: 'Okay Condition' },
          Bad: { icon: '/static/icons/circle-xmark-solid.svg', text: 'Bad Condition' },
          Poor: { icon: '/static/icons/xmark-solid.svg', text: 'Poor Condition' },
        };
        const { icon, text } = conditionMap[media.condition] || {
          icon: '/static/icons/circle-solid.svg',
          text: media.condition,
        };
        tags.push(
          <span className="tag" key="condition">
            <img src={icon} className="icon" alt={text} />
            {text}
          </span>
        );
      }
    }
    return tags.length > 0 ? tags : null;
  };

  return <div className="header-tags">{renderTags()}</div>;
};

export default MediaTags;